import { LandrBrand } from 'constants/brands';
import React from 'react';
import {
    useBreakpoint,
    DiskOutlined,
    StarOutlined,
    LogoOutlined,
    Tabs,
    Heading,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import some from 'lodash/some';
import {
    ChartsTopLabelsQuery,
    ChartsTopLandrPacksQuery,
    ChartsTopPacksQuery,
} from 'apollo/charts/charts.gql';
import { useQuery } from '@apollo/client';
import Slice from 'components/Slice/Slice';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { RoutePaths } from 'types/enums';
import { ChartEntry } from './Charts.types';
import { ChartsDesktopWrapper } from './Charts.styled';
import {
    LandrTopPacksChart,
    TopLabelsChart,
    TopPacksChart,
} from './components/Chart';

type ChartsInnerProps = {
    isLoading: boolean;
    isMobile: boolean;
    chartsTopPacks: ChartEntry[];
    chartsTopLabels: ChartEntry[];
    chartsTopLandrPacks: ChartEntry[];
};

export const _Charts: React.FC<ChartsInnerProps> = ({
    isLoading,
    isMobile,
    chartsTopPacks,
    chartsTopLabels,
    chartsTopLandrPacks,
}) => {
    const toLocalizedPath = useToLocalizedPath();

    const topPacksChartLink = toLocalizedPath(
        `/${RoutePaths.Browse}?tab=Packs`,
    );
    const topLabelsChartLink = toLocalizedPath(`/${RoutePaths.Labels}`);
    const landrTopPacksChartLink = toLocalizedPath(`/${RoutePaths.Exclusives}`);

    if (isMobile) {
        return (
            <Slice>
                <Heading as="h2" size="xl" mb="xxl">
                    <Trans>Charts</Trans>
                </Heading>
                <Tabs size="lg">
                    <Tabs.Pane label={<Trans>Packs</Trans>} name={t`Packs`}>
                        <TopPacksChart
                            isLoading={isLoading}
                            chart={chartsTopPacks}
                            linkPath={topPacksChartLink}
                        />
                    </Tabs.Pane>
                    <Tabs.Pane label={<Trans>Labels</Trans>} name={t`Labels`}>
                        <TopLabelsChart
                            isLoading={isLoading}
                            chart={chartsTopLabels}
                            linkPath={topLabelsChartLink}
                        />
                    </Tabs.Pane>
                    <Tabs.Pane
                        label={<Trans>{LandrBrand} exclusives</Trans>}
                        name={t`Exclusives`}
                    >
                        <LandrTopPacksChart
                            isLoading={isLoading}
                            chart={chartsTopLandrPacks}
                            linkPath={landrTopPacksChartLink}
                        />
                    </Tabs.Pane>
                </Tabs>
            </Slice>
        );
    }

    return (
        <Slice>
            <Heading as="h2" size="xl" mb="xxl" display="none">
                <Trans>Charts</Trans>
            </Heading>
            <ChartsDesktopWrapper gutter="mg">
                <TopPacksChart
                    isLoading={isLoading}
                    chart={chartsTopPacks}
                    heading={
                        <>
                            <DiskOutlined size="lg" />
                            <Trans>Trending packs</Trans>
                        </>
                    }
                    linkPath={topPacksChartLink}
                />
                <TopLabelsChart
                    isLoading={isLoading}
                    chart={chartsTopLabels}
                    heading={
                        <>
                            <StarOutlined size="lg" />
                            <Trans>Trending labels</Trans>
                        </>
                    }
                    linkPath={topLabelsChartLink}
                />
                <LandrTopPacksChart
                    isLoading={isLoading}
                    chart={chartsTopLandrPacks}
                    heading={
                        <>
                            <LogoOutlined size="lg" />
                            <Trans>Trending {LandrBrand} exclusives</Trans>
                        </>
                    }
                    linkPath={landrTopPacksChartLink}
                />
            </ChartsDesktopWrapper>
        </Slice>
    );
};

export const Charts = () => {
    const isMobile = useBreakpoint('lg');
    let { data: { chartsTopPacks } = {}, loading: packsLoading } =
        useQuery(ChartsTopPacksQuery);
    let { data: { chartsTopLabels } = {}, loading: labelsLoading } =
        useQuery(ChartsTopLabelsQuery);
    let { data: { chartsTopLandrPacks } = {}, loading: landrPacksLoading } =
        useQuery(ChartsTopLandrPacksQuery);

    chartsTopPacks = chartsTopPacks?.items ?? [];
    chartsTopLabels = chartsTopLabels?.items ?? [];
    chartsTopLandrPacks = chartsTopLandrPacks?.items ?? [];

    const isLoading = some([packsLoading, labelsLoading, landrPacksLoading]);

    return (
        <_Charts
            isLoading={isLoading}
            isMobile={isMobile}
            chartsTopPacks={chartsTopPacks}
            chartsTopLabels={chartsTopLabels}
            chartsTopLandrPacks={chartsTopLandrPacks}
        />
    );
};
